import { forwardRef, useEffect, useRef, useState } from 'react'
import { Application } from 'pixi.js'
import '@esotericsoftware/spine-pixi-v8'
import { BetsCycleLoader } from '@/components/ui/betscycle-loader'
import { CrashBetPanel } from '@/components/original/games/ui/CrashBetPanel'
import { AssetManager } from '@/components/original/games/crash/core/AssetManager'
import { GameController } from '@/components/original/games/crash/core/GameController'
import { SoundManager } from '@/components/original/games/crash/core/SoundManager'
import { Main } from '@/components/original/games/crash/main'
import { disconnectCrashSocket } from '@/store/socket/crash-socket'
import { cn } from '@/lib/utils'
import { useAppDispatch } from '@/store/hooks'
import { resetCrashGame, setCrashCurrencyCode } from '@/store/slices/crashSlice'

interface CrashGameProps {
	currencyCode: string
	className?: string
}

export const CrashGame = forwardRef<HTMLDivElement, CrashGameProps>(function CrashGame({ currencyCode, className }, ref) {
	const dispatch = useAppDispatch()
	const hostRef = useRef<HTMLDivElement>(null)
	const appRef = useRef<Application | null>(null)
	const controllerRef = useRef<GameController | null>(null)
	const [controller, setController] = useState<GameController | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [loadError, setLoadError] = useState(false)
	const [loadProgress, setLoadProgress] = useState(0)

	const setRootRef = (node: HTMLDivElement | null) => {
		if (typeof ref === 'function') {
			ref(node)
		} else if (ref) {
			ref.current = node
		}
	}

	useEffect(() => {
		dispatch(setCrashCurrencyCode(currencyCode))
	}, [currencyCode, dispatch])

	useEffect(() => {
		const syncRendererToHost = () => {
			const app = appRef.current
			const host = hostRef.current
			if (!app || !host) {
				return
			}

			const { clientWidth: width, clientHeight: height } = host
			if (width <= 0 || height <= 0) {
				return
			}

			app.renderer.resize(width, height)
		}

		document.addEventListener('fullscreenchange', syncRendererToHost)
		return () => document.removeEventListener('fullscreenchange', syncRendererToHost)
	}, [])

	useEffect(() => {
		let cancelled = false
		const host = hostRef.current

		if (!host) {
			return
		}

		const boot = async () => {
			setIsLoading(true)
			setLoadError(false)

			try {
				await AssetManager.loadAll((progress) => {
					if (!cancelled) {
						setLoadProgress(Math.round(progress * 100))
					}
				})
				await SoundManager.instance.load()

				if (cancelled || !hostRef.current) {
					return
				}

				const app = new Application()
				await app.init({
					background: '#000000',
					resizeTo: hostRef.current,
					antialias: true,
					autoDensity: true,
					resolution: Math.min(window.devicePixelRatio || 1, 2),
				})

				if (cancelled) {
					app.destroy(true)
					return
				}

				const canvas = app.canvas
				canvas.style.display = 'block'
				canvas.style.width = '100%'
				canvas.style.height = '100%'
				hostRef.current.appendChild(canvas)
				appRef.current = app

				const main = new Main(app)
				app.stage.addChild(main)

				const gameController = new GameController()
				controllerRef.current = gameController
				setController(gameController)

				SoundManager.instance.playMusic()
				setIsLoading(false)
			} catch {
				if (!cancelled) {
					setLoadError(true)
					setIsLoading(false)
				}
			}
		}

		void boot()

		return () => {
			cancelled = true
			controllerRef.current?.dispose()
			controllerRef.current = null
			setController(null)
			disconnectCrashSocket()
			SoundManager.instance.stopAllGameplay()

			if (appRef.current) {
				appRef.current.destroy(true)
				appRef.current = null
			}

			if (host) {
				host.replaceChildren()
			}

			dispatch(resetCrashGame())
		}
	}, [dispatch])

	return (
		<div
			ref={setRootRef}
			className={cn(
				'flex w-full min-w-0 flex-col gap-4 xl:flex-row',
				'[&:fullscreen]:fixed [&:fullscreen]:inset-0 [&:fullscreen]:z-[9999] [&:fullscreen]:flex [&:fullscreen]:h-screen [&:fullscreen]:w-screen [&:fullscreen]:flex-col [&:fullscreen]:gap-4 [&:fullscreen]:bg-background [&:fullscreen]:p-4 xl:[&:fullscreen]:flex-row',
				className,
			)}
		>
			<div className='order-2 h-[420px] w-full shrink-0 overflow-hidden sm:h-[520px] xl:order-1 xl:h-[520px] xl:w-[340px] 2xl:w-[360px] [:fullscreen_&]:order-2 [:fullscreen_&]:h-auto [:fullscreen_&]:max-h-[42%] [:fullscreen_&]:w-full [:fullscreen_&]:shrink-0 xl:[:fullscreen_&]:order-1 xl:[:fullscreen_&]:h-full xl:[:fullscreen_&]:max-h-none xl:[:fullscreen_&]:w-[340px]'>
				<CrashBetPanel controller={controller} className='h-full min-h-0 [:fullscreen_&]:h-full' />
			</div>

			<div
				className={cn(
					'relative order-1 w-full min-w-0 overflow-hidden rounded-2xl border border-border bg-card',
					// Slightly flatter than 1:1; stacked below xl, wide stage on desktop.
					'aspect-[6/5]',
					'xl:order-2 xl:aspect-auto xl:min-h-[520px] xl:flex-1',
					'[:fullscreen_&]:order-1 [:fullscreen_&]:aspect-auto [:fullscreen_&]:h-auto [:fullscreen_&]:min-h-0 [:fullscreen_&]:flex-1 xl:[:fullscreen_&]:order-2 xl:[:fullscreen_&]:h-full',
				)}
			>
				<div ref={hostRef} className='absolute inset-0 [&_canvas]:block [&_canvas]:h-full [&_canvas]:w-full' />

				{isLoading ? (
					<div className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-card/90'>
						<BetsCycleLoader size={32} />
						<p className='text-sm font-medium text-muted-foreground'>Loading crash assets… {loadProgress}%</p>
					</div>
				) : null}

				{loadError ? (
					<div className='absolute inset-0 z-10 flex items-center justify-center bg-card p-6'>
						<p className='text-sm text-muted-foreground'>Unable to load the crash game right now.</p>
					</div>
				) : null}
			</div>
		</div>
	)
})
